import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ArrowUpDown, Square, Columns2, Rows3, SlidersHorizontal, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WorkbenchFilters, ScoreFilter } from '@/hooks/useWorkbench';
import { RAIL_WIDTHS, type RailWidth } from '@/hooks/workbench/railWidthPreference';

// Der Typ wohnt bei der Persistenz (railWidthPreference.ts) und wird hier nur
// weitergereicht, damit alle bestehenden `import { type RailWidth } from
// './WorkbenchFilterBar'` gueltig bleiben.
export type { RailWidth };

/**
 * Ein Sinnbild je Breite. Bewusst ohne Text: die drei Knoepfe stehen
 * nebeneinander und tragen ihren Namen im `aria-label` — ausgeschriebene
 * Beschriftungen sprengten die Leiste auf schmalen Fenstern.
 */
const RAIL_WIDTH_ICONS: Record<RailWidth, typeof Square> = {
  hidden: Square,
  normal: Columns2,
  wide: Rows3,
};

const scoreFilterOptions: { key: string; value: ScoreFilter }[] = [
  { key: 'filterAll', value: 'all' },
  { key: 'filterCritical', value: 'critical' },
  { key: 'filterWarning', value: 'warning' },
  { key: 'filterGood', value: 'good' },
];

/**
 * Spec 9.2 ("wird davon abgeloest"): der Knopf "Alle ab 70 Punkten freigeben"
 * ist hier ersatzlos entfallen, samt seiner Rueckfrage, seinem K5-Fehlerzweig
 * und den vier Angaben, die ihn versorgt haben.
 *
 * Die Sammelfreigabe sitzt jetzt im Menue "Freigeben" in der Kopfzeile der
 * Seite (`WorkbenchReleaseMenu`). Der Grund ist nicht nur die Platzierung:
 * der alte Knopf kannte genau EINE Menge (Punktzahl ab 70) und holte sie sich
 * ueber eine Keyset-Paginierung in 1000er-Seiten, auf dem groessten Feed also
 * 62 Anfragen, nur um eine Zahl an eine Beschriftung zu schreiben. Das Menue
 * bekommt alle fuenf Zahlen aus EINEM Aufruf und kann drei Mengen anbieten.
 *
 * Die Filterleiste filtert seitdem wieder ausschliesslich - sie fasst keine
 * Daten mehr an.
 */
interface WorkbenchFilterBarProps {
  filters: WorkbenchFilters;
  onFiltersChange: (filters: WorkbenchFilters) => void;
  railWidth: RailWidth;
  onRailWidthChange: (width: RailWidth) => void;
  /**
   * Verdichtete Fassung fuer die einzeilige Kopfzone.
   *
   * WARUM ES DIESE FASSUNG BRAUCHT, gemessen und nicht geschaetzt: die
   * ausgeschriebene Leiste belegt 1189 px. Neben den Reitern (335) und den
   * beiden Aktionsknoepfen (174) bleiben ihr selbst bei 1500 px Container nur
   * rund 950 px, und sie bricht um - die "eine Zeile" war in JEDER
   * Fensterbreite drei Zeilen hoch. Verdichtet braucht sie 350 px
   * (Suchfeld 240 plus Filtermenue 110), und die Zeile bleibt eine Zeile.
   *
   * Was ins Menue wandert, ist genau das, was selten angefasst wird: die vier
   * Punkte-Baender, der Hinweis-Schalter und die Sortierung. Was sichtbar
   * bleibt, ist die Suche und die Leistenbreite.
   */
  kompakt?: boolean;
}

export function WorkbenchFilterBar({
  filters,
  onFiltersChange,
  railWidth,
  onRailWidthChange,
  kompakt = false,
}: WorkbenchFilterBarProps) {
  const { t } = useTranslation('workbench');
  const [searchInput, setSearchInput] = useState(filters.search);

  /**
   * Der Debounce muss den AKTUELLEN Filterstand aufsetzen, nicht den aus dem
   * Render, in dem der letzte Tastenanschlag fiel.
   *
   * Vorher las der setTimeout-Rumpf `filters` aus seiner Closure. Wer tippte
   * und innerhalb von 300 ms eine Score-Pille, den Hinweis-Schalter oder die
   * Sortierung umstellte, bekam diese Aenderung vom feuernden Timer wieder
   * zurueckgesetzt - der Aufraeumer laeuft nur bei einem erneuten
   * Tastenanschlag, eine reine Filteraenderung loescht ihn nicht.
   */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      const aktuell = filtersRef.current;
      if (searchInput !== aktuell.search) {
        onFiltersChange({ ...aktuell, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, onFiltersChange]);

  // Externer Filter-Reset (z.B. "Filter zuruecksetzen" im leergefilterten
  // Leerzustand) muss das Suchfeld mitziehen — der Debounce-Effect oben laeuft
  // nur bei Tipp-Eingaben. Waehrend des Tippens feuert dieser Effect nicht
  // (filters.search aendert sich erst NACH dem Debounce, und dann auf genau
  // den getippten Wert — das setSearchInput ist dann ein No-op).
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Wieviele Filter vom Standard abweichen. Die Zahl steht am Menue-Knopf,
  // damit eine aktive Verkuerzung der Liste nicht unsichtbar wird - im Menue
  // verborgen ist ein Filter sonst genau das.
  //
  // `awaitingUploadOnly` zaehlt hier mit (Task 5, Spec 5.2), obwohl er sich
  // ueber diese Leiste gar nicht EINSCHALTEN laesst (nur per URL-Deep-Link) —
  // die Zahl beschreibt die aktuell wirkende Verkuerzung, nicht nur die hier
  // erreichbaren Schalter.
  const aktiveFilter =
    (filters.scoreFilter !== 'all' ? 1 : 0) +
    (filters.warningsOnly ? 1 : 0) +
    (filters.awaitingUploadOnly ? 1 : 0) +
    (filters.ruleId ? 1 : 0) +
    (filters.sortOrder !== 'desc' ? 1 : 0);

  /**
   * Die Pille "noch nicht bei Google" (Task 5, Spec 5.2).
   *
   * Anders als der Beanstandungs-Schalter gibt es in dieser Leiste keinen
   * Weg, den Filter EINZUSCHALTEN — er kommt ausschliesslich per URL
   * (`scope=awaiting_upload`, gesetzt von einem Journey-Chip-Klick oder
   * einem Deep-Link). Die Pille ist deshalb rein abwaehlbar und nur
   * sichtbar, solange der Filter aktiv ist: ohne ein sichtbares Gegenstueck
   * saesse der Nutzer nach dem Klick vor einer verkuerzten Liste ohne Ausweg
   * (dasselbe Muster wie beim Beanstandungs-Schalter oben, siehe dessen
   * Kommentar).
   */
  const awaitingUploadPill = filters.awaitingUploadOnly ? (
    <button
      type="button"
      aria-pressed
      onClick={() => onFiltersChange({ ...filters, awaitingUploadOnly: false })}
      data-testid="workbench-filter-awaiting-upload"
      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground transition-colors"
    >
      {t('filters.awaitingUpload')}
      <X className="h-3.5 w-3.5" />
    </button>
  ) : null;

  /**
   * WP5 A2O-02: die Regel kommt nur per URL (`rule=`, gesetzt vom Feed-Check).
   * Wie die Pille darueber nur abwaehlbar, damit eine verkuerzte Liste nie
   * ohne sichtbaren Grund und ohne Ausweg dasteht.
   */
  const rulePill = filters.ruleId ? (
    <button
      type="button"
      aria-pressed
      onClick={() => onFiltersChange({ ...filters, ruleId: null })}
      data-testid="workbench-filter-rule"
      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground transition-colors"
    >
      {t(`filters.rule.${filters.ruleId}`)}
      <X className="h-3.5 w-3.5" />
    </button>
  ) : null;

  if (kompakt) {
    return (
      <div className="flex items-center gap-2" data-testid="workbench-filter-kompakt">
        <div className="relative w-[240px] flex-shrink-0" data-testid="workbench-filter-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/70" />
          <Input
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-10 pl-9"
          />
        </div>

        {awaitingUploadPill}
        {rulePill}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-10 flex-shrink-0 gap-1.5"
              data-testid="workbench-filter-menu"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('filters.menu')}
              {aktiveFilter > 0 && (
                <span className="ml-0.5 rounded-md bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground tabular-nums">
                  {aktiveFilter}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{t('filters.scoreBands')}</DropdownMenuLabel>
            {scoreFilterOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onSelect={() => onFiltersChange({ ...filters, scoreFilter: opt.value })}
                className="justify-between"
              >
                {t(`filters.${opt.key}`)}
                {filters.scoreFilter === opt.value && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {/* Eigene Dimension, kein fuenftes Band: der Schalter laesst sich
                mit jedem Band verbinden. Deshalb steht er unter einem Strich
                und nicht in der Gruppe darueber. */}
            <DropdownMenuItem
              onSelect={() => onFiltersChange({ ...filters, warningsOnly: !filters.warningsOnly })}
              className="justify-between"
              data-testid="workbench-filter-warnings-only"
            >
              {t('filters.warningsOnly')}
              {filters.warningsOnly && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                onFiltersChange({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })
              }
              className="gap-2"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              {filters.sortOrder === 'asc' ? t('filters.lowestFirst') : t('filters.highestFirst')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Die Leistenbreite bleibt sichtbar: sie aendert, WAS man sieht,
            nicht welche Teilmenge. Im Menue verborgen fiele sie aus dem
            Blick, obwohl sie der haeufigste Griff der ganzen Leiste ist. */}
        {/* W1 (Spec 4.1): genau EIN gefuellter Knopf je Bildschirm, und das
            ist "Freigeben" in der Fusszeile der Karte. Der gewaehlte
            Ansichts-Schalter trug bis zum 08.09.2026 `variant="default"`,
            also dieselbe gefuellte Petrol-Flaeche wie die Primaerhandlung -
            gemessen als zweiter gefuellter Knopf oben in der Kopfzeile.
            Ein AUSGEWAEHLTER Zustand ist aber keine Handlung, und die
            Auswahl braucht dafuer auch keine Signalfarbe: `bg-muted p-1`
            aussen, `bg-card shadow-sm` innen ist das Hausmuster aus
            `ui/tabs.tsx`, das die Statusreiter daneben bereits benutzen. */}
        <div
          className="flex flex-shrink-0 gap-0.5 rounded-md bg-muted p-1"
          role="group"
          aria-label={t('railWidth.label')}
          data-testid="workbench-rail-width-switch"
        >
          {RAIL_WIDTHS.map((width) => {
            const Icon = RAIL_WIDTH_ICONS[width];
            const aktiv = railWidth === width;
            return (
              <Button
                key={width}
                size="sm"
                variant="ghost"
                aria-pressed={aktiv}
                onClick={() => onRailWidthChange(width)}
                className={
                  aktiv
                    ? 'h-7 w-7 p-0 bg-card text-foreground shadow-sm hover:bg-card'
                    : 'h-7 w-7 p-0 text-muted-foreground'
                }
                aria-label={t(`railWidth.${width}`)}
              >
                <Icon className="h-4 w-4" />
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-4 pb-4">
          {/* The action row only fits next to the search field from ~1024px upwards
              (bulk-accept + sort + view toggle together need ~650px). Below that the
              search gets its own line and the actions wrap underneath, so every
              control stays inside the viewport instead of overflowing into the
              `overflow-x: hidden` of <main>.

              The three separators are `hidden lg:block` for the same reason: while
              the row wraps, a 1px stroke can end up as the first or last item of a
              line and stand there without the neighbours it is supposed to divide.
              They only appear once the row is a single line beside the search. */}
          <div
            className="flex flex-col lg:flex-row gap-3"
            data-testid="workbench-filter-layout"
          >
            {/* Search */}
            <div className="relative flex-1 min-w-0" data-testid="workbench-filter-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('filters.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Score filter pills + Bulk-accept + Sort toggle */}
            <div className="flex flex-wrap items-center gap-3" data-testid="workbench-filter-actions">
              <div className="flex flex-wrap gap-1.5" data-testid="workbench-filter-pills">
                {scoreFilterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onFiltersChange({ ...filters, scoreFilter: opt.value })}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      filters.scoreFilter === opt.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground/70 hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    {t(`filters.${opt.key}`)}
                  </button>
                ))}
              </div>

              {/* Beanstandungen (Spec 9.1). Steht NEBEN der Pillengruppe und
                  nicht darin: die vier Punkte-Pillen sind ein Entweder-oder
                  ueber ein Band der Gesamtpunktzahl, dieser Schalter ist eine
                  eigene Dimension und laesst sich mit jedem Band verbinden.
                  Als fuenfte Pille in der Gruppe saehe er wie ein weiteres
                  Band aus und behauptete ein Entweder-oder, das es nicht gibt.

                  Er MUSS sichtbar sein: der Menueeintrag "N mit Hinweisen
                  zuerst ansehen" setzt ihn von aussen. Ohne ein sichtbares
                  Gegenstueck saesse der Nutzer vor einer still verkuerzten
                  Liste und faende keinen Weg zurueck.

                  `aria-pressed` statt der stummen Farbumschaltung der Pillen:
                  ein Schalter mit zwei Zustaenden muss ihn auch ansagen,
                  sonst ist "aktiv" allein eine Farbe. */}
              <button
                type="button"
                aria-pressed={filters.warningsOnly}
                onClick={() =>
                  onFiltersChange({ ...filters, warningsOnly: !filters.warningsOnly })
                }
                data-testid="workbench-filter-warnings-only"
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  filters.warningsOnly
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground/70 hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {t('filters.warningsOnly')}
              </button>

              {awaitingUploadPill}
              {rulePill}

              {/* Hier stand bis Etappe 4 der Sammelfreigabe-Knopf samt seinem
                  K5-Fehlerzweig und dem eigenen Trenner davor. Beides ist mit
                  ihm gegangen (Begruendung am Props-Block oben), deshalb
                  bleiben in dieser Reihe noch ZWEI Trenner statt drei. */}
              <div className="hidden lg:block h-5 w-px bg-border" data-testid="workbench-filter-separator" />

              <button
                onClick={() => onFiltersChange({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                {filters.sortOrder === 'asc' ? t('filters.lowestFirst') : t('filters.highestFirst')}
              </button>

              <div className="hidden lg:block h-5 w-px bg-border" data-testid="workbench-filter-separator" />

              {/* Leistenbreite: drei Stufen statt zwei Modi (Task B2). Die
                  beiden Enden sind die frueheren Modi — `hidden` ist exakt
                  die alte Karte, `wide` exakt die alte Liste. Es geht also
                  nichts verloren, es kommt eine Zwischenstufe dazu. */}
              {/* Dieselbe Segment-Auswahl wie in der verdichteten Fassung
                  oben (W1): ein ausgewaehlter Zustand ist keine Handlung und
                  darf nicht wie die Primaerhandlung aussehen. Beide Fassungen
                  muessen die Regel halten, sonst haengt sie am Schalter
                  `kopfzone` statt an der Seite. */}
              <div
                className="flex gap-0.5 rounded-md bg-muted p-1"
                role="group"
                aria-label={t('railWidth.label')}
                data-testid="workbench-rail-width-switch"
              >
                {RAIL_WIDTHS.map((width) => {
                  const Icon = RAIL_WIDTH_ICONS[width];
                  const aktiv = railWidth === width;
                  return (
                    <Button
                      key={width}
                      size="sm"
                      variant="ghost"
                      aria-pressed={aktiv}
                      onClick={() => onRailWidthChange(width)}
                      className={
                        aktiv
                          ? 'h-7 w-7 p-0 bg-card text-foreground shadow-sm hover:bg-card'
                          : 'h-7 w-7 p-0 text-muted-foreground'
                      }
                      aria-label={t(`railWidth.${width}`)}
                    >
                      <Icon className="h-4 w-4" />
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
